import moment from 'moment';

/**
 * Date-only fields (start / install / due dates) are calendar dates, not clock times.
 * Backend stores them as UTC midnight of that day — always format with UTC so
 * US, India, Europe, NZ, etc. all see the same MM/DD/YYYY.
 */
export function formatDateOnly(unixOrMoment, pattern = 'MM/DD/YYYY') {
  if (unixOrMoment == null || unixOrMoment === '') return '';
  const m =
    typeof unixOrMoment === 'number' || /^\d+$/.test(String(unixOrMoment))
      ? moment.unix(Number(unixOrMoment)).utc()
      : moment.utc(unixOrMoment);
  return m.isValid() ? m.format(pattern) : '';
}

export function dateOnlyMoment(unix) {
  if (unix == null || unix === '') return null;
  const local = moment.unix(Number(unix));
  if (!local.isValid()) return null;

  const utc = local.clone().utc();
  const isUtcMidnight =
    utc.hours() === 0 && utc.minutes() === 0 && utc.seconds() === 0;

  // Stored date-only values are UTC midnight of the calendar day.
  if (isUtcMidnight) {
    return moment(utc.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day');
  }

  // Real timestamps (e.g. legacy moment().unix() seeds): use the user's local
  // calendar day so UTC-11 / US evenings don't jump to "tomorrow".
  return local.clone().startOf('day');
}

/** Persist DatePicker selection as UTC midnight unix for that calendar day. */
export function dateOnlyToUnix(date) {
  if (!date) return null;
  const m = moment(date);
  if (!m.isValid()) return null;
  return moment.utc(m.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').unix();
}

/** Local calendar "today" as UTC-midnight unix (safe for date-only form defaults). */
export function localTodayDateOnlyUnix() {
  return dateOnlyToUnix(moment());
}

/**
 * Normalize any unix/date into a date-only UTC-midnight unix.
 * - Already UTC midnight → keep that calendar day
 * - Real timestamps → local calendar day (avoids UTC-11 evening → next UTC day)
 */
export function toDateOnlyUnix(unixOrDate) {
  if (unixOrDate == null || unixOrDate === '') return null;
  if (moment.isMoment(unixOrDate) || unixOrDate instanceof Date) {
    return dateOnlyToUnix(unixOrDate);
  }
  const local = moment.unix(Number(unixOrDate));
  if (!local.isValid()) return null;
  const utc = local.clone().utc();
  if (utc.hours() === 0 && utc.minutes() === 0 && utc.seconds() === 0) {
    return utc.startOf('day').unix();
  }
  return dateOnlyToUnix(local);
}

/**
 * Estimate/Order "document date" picker value from created_at / similar unix.
 * - Real timestamps → local calendar day (avoids UTC day rolling into "tomorrow" for US evenings)
 * - UTC-midnight date-only values → same UTC calendar day as dateOnlyMoment (stable after save/reload)
 */
export function estimateDatePickerMoment(unix) {
  if (unix == null || unix === '') return null;
  const local = moment.unix(Number(unix));
  if (!local.isValid()) return null;

  const utc = local.clone().utc();
  const isUtcMidnight =
    utc.hours() === 0 && utc.minutes() === 0 && utc.seconds() === 0;

  if (isUtcMidnight) {
    return moment(utc.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day');
  }
  return local.clone().startOf('day');
}
